import { useCallback, useEffect, useState } from "react";
import { message } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";

interface SaleDetailLinkOptions {
  api: {
    fetchVenteDetail: (
      id: number,
      scope: { company_id: number; warehouse_id: number },
    ) => Promise<any>;
  };
  active?: boolean;
  onOpen: (sale: any) => void;
  resetDetail: () => void;
}

const positiveId = (value: any): number | null => {
  const id = Number(value?.id ?? value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

// Les liens depuis une proforma ciblent une vente, indépendamment de la liste paginée.
export default function useSaleDetailLink({
  api,
  active = true,
  onOpen,
  resetDetail,
}: SaleDetailLinkOptions) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const { selectedCompany, selectedWarehouse } = useSelection();
  const userId = positiveId(user?.id);
  const companyId = positiveId(selectedCompany);
  const warehouseId = positiveId(selectedWarehouse);
  const requestedId = new URLSearchParams(location.search).get("id");
  const canView = hasPermission("Gestion Commerciale.Ventes.Ventes.view");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!active || requestedId === null) return;

    let current = true;
    resetDetail();
    setLoading(false);
    if (!userId || !companyId || !warehouseId || !canView) return;

    const saleId = positiveId(requestedId);
    if (!saleId || !/^[1-9]\d*$/.test(requestedId)) {
      message.error("Le lien ne contient pas un identifiant de vente valide.");
      return;
    }

    setLoading(true);
    const openSale = async () => {
      try {
        const sale = await api.fetchVenteDetail(saleId, {
          company_id: companyId,
          warehouse_id: warehouseId,
        });
        if (!current) return;

        if (
          positiveId(sale?.id) !== saleId ||
          positiveId(sale?.company_id) !== companyId ||
          positiveId(sale?.warehouse_id) !== warehouseId ||
          !["sales", "sale", "pos"].includes(String(sale?.order_type).toLowerCase())
        ) {
          throw new Error("Vente hors du contexte demandé");
        }
        onOpen(sale);
      } catch {
        if (current) {
          message.error(
            "Impossible d'ouvrir la vente demandée. Vérifiez votre connexion et l'accès à cette vente dans l'entreprise et l'établissement sélectionnés.",
          );
        }
      } finally {
        if (current) setLoading(false);
      }
    };
    void openSale();

    return () => {
      current = false;
      setLoading(false);
      resetDetail();
    };
  }, [api, active, requestedId, userId, companyId, warehouseId, canView, onOpen, resetDetail]);

  const clearLink = useCallback(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has("id")) return;
    params.delete("id");
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : "", hash: location.hash },
      { replace: true, state: location.state },
    );
  }, [location.pathname, location.search, location.hash, location.state, navigate]);

  return { loading, clearLink };
}
